import {
  useGetHirerTeamMemberJobAccessAccessList,
  type GetHirerTeamMemberJobAccessListResult,
  type TeamMemberForJobAccess,
} from '@seek/hirer-team-member-job-access';
import { useTranslations } from '@vocab/react';
import { Box, Notice, Text, TextLink } from 'braid-design-system';
import { useState, useEffect, type FC, type PropsWithChildren } from 'react';

import { useConfig } from 'src/hooks/useConfig';
import { getPhoneNumber } from 'src/modules/localisation/customerService';
import { isSiteAnz } from 'src/siteConfig';
import { getContactUsUrl } from 'src/utils/routes';

import translations from './.vocab';
import { AddTeamMemberView } from './AddTeamMemberView';
import { TeamMemberAccessStepper } from './TeamMemberAccessStepper';

interface Props {
  onSubmitSuccess(): void;
  onCancel(): void;
  jobTitle?: string;
  jobId?: string;
}

interface TeamMemberAccessState {
  hasError: boolean;
  teamMembers: TeamMemberForJobAccess[];
  hasLoadedTeamMembers: boolean;
}

export const TeamMemberAccess: FC<PropsWithChildren<Props>> = ({
  onSubmitSuccess,
  onCancel,
  jobTitle,
  jobId,
}: Props) => {
  const [state, setState] = useState<TeamMemberAccessState>({
    hasError: false,
    teamMembers: [],
    hasLoadedTeamMembers: false,
  });

  const { site, urlResolver } = useConfig();
  const { t } = useTranslations(translations);

  const isAnz = isSiteAnz(site);
  const csPhoneNumber = getPhoneNumber(site);
  const contactUsUrl = getContactUsUrl(urlResolver);
  const getHirerTeamMemberJobAccessAccessList =
    useGetHirerTeamMemberJobAccessAccessList();

  useEffect(() => {
    if (!state.hasLoadedTeamMembers) {
      getHirerTeamMemberJobAccessAccessList(
        ({
          error,
          teamMemberJobAccessList,
        }: GetHirerTeamMemberJobAccessListResult) => {
          setState((prevState) => ({
            ...prevState,
            hasLoadedTeamMembers: true,
            teamMembers: teamMemberJobAccessList,
            hasError: Boolean(error),
          }));
        },
      );
    }
  });

  const AnzMessage = () => (
    <Text>
      {t('Sorry we are having trouble...anz', {
        CustomerServicePhoneNumber: () => (
          <TextLink key="csphone" href={`tel:${csPhoneNumber}`}>
            {csPhoneNumber}
          </TextLink>
        ),
      })}
    </Text>
  );

  const AsiaMessage = () => (
    <Text>
      {t('Sorry we are having trouble...asia', {
        Link: (children) => (
          <TextLink key="cslink" href={`${contactUsUrl}`}>
            {children}
          </TextLink>
        ),
      })}
    </Text>
  );

  if (state.hasError) {
    return (
      <Box paddingX="large">
        <Notice tone="critical">
          {isAnz ? <AnzMessage /> : <AsiaMessage />}
        </Notice>
      </Box>
    );
  }

  if (state.hasLoadedTeamMembers && state.teamMembers?.length === 0) {
    return <AddTeamMemberView />;
  }

  return (
    <TeamMemberAccessStepper
      onComplete={onSubmitSuccess}
      onCancel={onCancel}
      teamMembers={state.teamMembers}
      jobId={`${jobId}`}
      jobTitle={`${jobTitle}`}
    />
  );
};
